<template>
  <div class="product-detail-page" v-if="product">
    <section class="product-detail">
      <div class="container">
        <div class="product-layout">
          <!-- Product Images -->
          <div class="product-images">
            <div class="main-image">
              <img 
                :src="currentImage?.url || product.images?.[0]?.url || product.image" 
                :alt="currentImage?.alt || product.name" 
                loading="lazy"
              >
            </div>
            <div class="image-thumbnails" v-if="product.images && product.images.length > 1">
              <button 
                v-for="(image, index) in product.images" 
                :key="index"
                :class="['thumbnail-btn', { active: currentImageIndex === index }]"
                @click="currentImageIndex = index"
              >
                <img :src="image.url" :alt="image.alt || product.name" loading="lazy">
              </button>
            </div>
          </div>

          <!-- Product Info -->
          <div class="product-info">
            <div class="product-header">
              <h1>{{ product.name }}</h1>
              <div class="product-meta">
                <span v-if="product.sku" class="sku">SKU: {{ product.sku }}</span>
                <span v-if="product.rating?.count > 0" class="rating">
                  ★ {{ product.rating.average.toFixed(1) }} ({{ product.rating.count }} reviews)
                </span>
              </div>
            </div>

            <div class="product-pricing">
              <div class="price-container">
                <span class="current-price">${{ selectedSizePrice.toLocaleString() }}</span>
                <span v-if="product.originalPrice && product.originalPrice > selectedSizePrice" class="original-price">
                  ${{ product.originalPrice.toLocaleString() }}
                </span>
                <span v-if="discountPercentage > 0" class="discount-badge">
                  -{{ discountPercentage }}%
                </span>
              </div>
            </div>

            <p class="product-description">{{ product.description }}</p>

            <!-- Size Selection -->
            <div class="size-selection" v-if="product.sizes && product.sizes.length > 0">
              <h3>Select Size</h3>
              <div class="size-options">
                <button 
                  v-for="size in product.sizes" 
                  :key="size.name"
                  :class="['size-btn', { 
                    active: selectedSize?.name === size.name,
                    'out-of-stock': size.stock <= 0
                  }]"
                  @click="selectSize(size)"
                  :disabled="size.stock <= 0"
                >
                  <span class="size-name">{{ size.name }}</span>
                  <span class="size-price">${{ size.price.toLocaleString() }}</span>
                  <span v-if="size.stock <= 0" class="stock-status">Out of Stock</span>
                </button>
              </div>
            </div>

            <!-- Quantity Selection -->
            <div class="quantity-selection" v-if="selectedSize && selectedSize.stock > 0">
              <h3>Quantity</h3>
              <div class="quantity-controls">
                <button @click="decreaseQuantity" :disabled="quantity <= 1" class="qty-btn">
                  -
                </button>
                <span class="quantity">{{ quantity }}</span>
                <button @click="increaseQuantity" :disabled="quantity >= selectedSize.stock" class="qty-btn">
                  +
                </button>
              </div>
              <p v-if="selectedSize.stock <= 10" class="low-stock-warning">
                Only {{ selectedSize.stock }} left in stock!
              </p>
            </div>

            <!-- Add to Cart -->
            <div class="add-to-cart" v-if="selectedSize && selectedSize.stock > 0">
              <button @click="addToCart" class="btn btn-large" :disabled="!selectedSize">
                Add to Cart - ${{ (selectedSizePrice * quantity).toLocaleString() }}
              </button>
            </div>

            <!-- Product Features -->
            <div class="product-features" v-if="product.features && product.features.length > 0">
              <h3>Features</h3>
              <div class="features-grid">
                <div v-for="feature in product.features" :key="feature.title" class="feature-item">
                  <div v-if="feature.icon" class="feature-icon">
                    <i :class="feature.icon"></i>
                  </div>
                  <div class="feature-content">
                    <h4>{{ feature.title }}</h4>
                    <p>{{ feature.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Product Specifications -->
            <div class="product-specs" v-if="product.specifications">
              <h3>Specifications</h3>
              <div class="specs-grid">
                <div v-if="product.specifications.dimensions" class="spec-item">
                  <span class="spec-label">Dimensions:</span>
                  <span class="spec-value">{{ product.specifications.dimensions }}</span>
                </div>
                <div v-if="product.specifications.weight" class="spec-item">
                  <span class="spec-label">Weight:</span>
                  <span class="spec-value">{{ product.specifications.weight }}</span>
                </div>
                <div v-if="product.specifications.material" class="spec-item">
                  <span class="spec-label">Material:</span>
                  <span class="spec-value">{{ product.specifications.material }}</span>
                </div>
                <div v-if="product.specifications.warranty" class="spec-item">
                  <span class="spec-label">Warranty:</span>
                  <span class="spec-value">{{ product.specifications.warranty }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Related Products -->
    <section class="related-products section-bg" v-if="relatedProducts.length > 0">
      <div class="container">
        <h2>You might also like</h2>
        <div class="related-grid">
          <div 
            class="product-card" 
            v-for="related in relatedProducts" 
            :key="related._id || related.id"
          >
            <img 
              :src="related.images?.[0]?.url || related.image" 
              :alt="related.name" 
              loading="lazy"
            >
            <div class="product-card-content">
              <h4>{{ related.name }}</h4>
              <p class="price">${{ related.price.toLocaleString() }}</p>
              <router-link :to="`/product/${related._id || related.id}`" class="btn btn-outline">
                View Details
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <div v-else-if="loading" class="loading">
    <h3>Loading product...</h3>
    <p>Please wait while we fetch the product details.</p>
  </div>

  <div v-else-if="error" class="error">
    <h3>Error loading product</h3>
    <p>{{ error }}</p>
    <button @click="loadProduct" class="btn">Try Again</button>
  </div>

  <div v-else class="not-found">
    <h3>Product not found</h3>
    <p>The product you're looking for doesn't exist.</p>
    <router-link to="/products" class="btn">Back to Products</router-link>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useCartStore } from '../stores/cart'
import { useProductsStore } from '../stores/products'

export default {
  name: 'ProductDetail',
  setup() {
    const route = useRoute()
    const cartStore = useCartStore()
    const productsStore = useProductsStore()

    // Reactive data
    const selectedSize = ref(null)
    const quantity = ref(1)
    const currentImageIndex = ref(0)

    // Computed properties
    const product = computed(() => {
      return productsStore.currentProduct
    })

    const currentImage = computed(() => {
      if (!product.value?.images) return null
      return product.value.images[currentImageIndex.value]
    })

    const selectedSizePrice = computed(() => {
      if (selectedSize.value) {
        return selectedSize.value.price
      }
      return product.value?.price || 0
    })

    const discountPercentage = computed(() => {
      if (!product.value?.originalPrice || !selectedSizePrice.value) return 0
      if (product.value.originalPrice > selectedSizePrice.value) {
        return Math.round(((product.value.originalPrice - selectedSizePrice.value) / product.value.originalPrice) * 100)
      }
      return 0
    })

    const relatedProducts = computed(() => {
      if (!product.value) return []
      
      return productsStore.products
        .filter(p => p.category === product.value.category && p._id !== product.value._id)
        .slice(0, 3)
    })

    // Methods
    const selectSize = (size) => {
      selectedSize.value = size
      quantity.value = 1 // Reset quantity when size changes
    }

    const increaseQuantity = () => {
      if (selectedSize.value && quantity.value < selectedSize.value.stock) {
        quantity.value++
      }
    }

    const decreaseQuantity = () => {
      if (quantity.value > 1) {
        quantity.value--
      }
    }

    const addToCart = () => {
      if (product.value && selectedSize.value) {
        const cartItem = {
          product: product.value,
          size: selectedSize.value.name,
          quantity: quantity.value,
          price: selectedSize.value.price
        }
        cartStore.addToCart(cartItem)
        alert(`${product.value.name} (${selectedSize.value.name}) added to cart!`)
      }
    }

    const loadProduct = async () => {
      try {
        await productsStore.fetchProduct(route.params.id)
        // Auto-select first available size
        if (product.value?.sizes && product.value.sizes.length > 0) {
          const firstAvailableSize = product.value.sizes.find(size => size.stock > 0)
          if (firstAvailableSize) {
            selectedSize.value = firstAvailableSize
          }
        }
      } catch (error) {
        console.error('Error loading product:', error)
      }
    }

    // Load product on mount
    onMounted(async () => {
      await loadProduct()
    })

    return {
      product,
      relatedProducts,
      selectedSize,
      quantity,
      currentImageIndex,
      currentImage,
      selectedSizePrice,
      discountPercentage,
      selectSize,
      increaseQuantity,
      decreaseQuantity,
      addToCart,
      loadProduct,
      loading: productsStore.loading,
      error: productsStore.error
    }
  }
}
</script>

<style scoped>
.product-detail-page {
  min-height: 100vh;
}

.product-detail {
  padding: 60px 0;
}

.product-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;
}

.product-images {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.main-image img {
  width: 100%;
  height: 500px;
  object-fit: cover;
  border-radius: 12px;
}

.image-thumbnails {
  display: flex;
  gap: 10px;
  overflow-x: auto;
}

.thumbnail-btn {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: none;
  padding: 0;
}

.thumbnail-btn img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-btn:hover {
  border-color: var(--primary-color);
}

.thumbnail-btn.active {
  border-color: var(--primary-color);
}

.product-header {
  margin-bottom: 1.5rem;
}

.product-header h1 {
  margin-bottom: 0.5rem;
  font-size: 2.5rem;
}

.product-meta {
  display: flex;
  gap: 20px;
  font-size: 0.9rem;
  color: var(--text-medium);
}

.sku {
  font-family: monospace;
  background: var(--bg-light);
  padding: 4px 8px;
  border-radius: 4px;
}

.rating {
  color: #ffc107;
}

.product-pricing {
  margin-bottom: 2rem;
}

.price-container {
  display: flex;
  align-items: center;
  gap: 15px;
}

.current-price {
  color: var(--primary-color);
  font-weight: 700;
  font-size: 2rem;
}

.original-price {
  color: var(--text-medium);
  text-decoration: line-through;
  font-size: 1.2rem;
}

.discount-badge {
  background: #dc3545;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.product-description {
  color: var(--text-medium);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.size-selection,
.quantity-selection {
  margin-bottom: 2rem;
}

.size-selection h3,
.quantity-selection h3 {
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.size-options {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.size-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 20px;
  border: 2px solid var(--border-color);
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  min-width: 120px;
}

.size-btn:hover:not(:disabled) {
  border-color: var(--primary-color);
}

.size-btn.active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.size-btn.out-of-stock {
  opacity: 0.5;
  cursor: not-allowed;
}

.size-name {
  font-weight: 600;
  margin-bottom: 4px;
}

.size-price {
  font-size: 0.9rem;
  color: var(--primary-color);
}

.stock-status {
  font-size: 0.8rem;
  color: #dc3545;
  margin-top: 4px;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.qty-btn {
  width: 40px;
  height: 40px;
  border: 2px solid var(--border-color);
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.qty-btn:hover:not(:disabled) {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: white;
}

.qty-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity {
  font-size: 1.2rem;
  font-weight: 600;
  min-width: 30px;
  text-align: center;
}

.low-stock-warning {
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 8px;
  font-weight: 500;
}

.add-to-cart {
  margin-bottom: 2rem;
}

.btn-large {
  width: 100%;
  padding: 16px 32px;
  font-size: 1.1rem;
}

.product-features {
  margin-bottom: 2rem;
}

.product-features h3 {
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.features-grid {
  display: grid;
  gap: 15px;
}

.feature-item {
  display: flex;
  gap: 15px;
  padding: 15px;
  background: var(--bg-light);
  border-radius: 8px;
}

.feature-icon {
  font-size: 1.5rem;
  color: var(--primary-color);
  min-width: 30px;
}

.feature-content h4 {
  margin-bottom: 5px;
  font-size: 1rem;
}

.feature-content p {
  color: var(--text-medium);
  font-size: 0.9rem;
  margin: 0;
}

.product-specs {
  margin-bottom: 2rem;
}

.product-specs h3 {
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.specs-grid {
  display: grid;
  gap: 10px;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-color);
}

.spec-label {
  font-weight: 600;
  color: var(--text-dark);
}

.spec-value {
  color: var(--text-medium);
}

.related-products {
  padding: 60px 0;
}

.related-products h2 {
  text-align: center;
  margin-bottom: 2rem;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.loading,
.error,
.not-found {
  text-align: center;
  padding: 100px 0;
}

.loading h3,
.error h3,
.not-found h3 {
  margin-bottom: 1rem;
  color: var(--text-medium);
}

.error {
  color: #dc3545;
}

.error .btn,
.not-found .btn {
  margin-top: 1rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
}

.error .btn:hover,
.not-found .btn:hover {
  background-color: var(--primary-dark);
}

@media (max-width: 768px) {
  .product-layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  
  .product-header h1 {
    font-size: 2rem;
  }
  
  .size-options {
    justify-content: center;
  }
  
  .quantity-controls {
    justify-content: center;
  }
  
  .related-grid {
    grid-template-columns: 1fr;
  }

  .image-thumbnails {
    justify-content: center;
  }
}
</style> 